//Codes of the errors the app raises itself, next to the identity providers' own. None of them contains a
//substring that isAuthInteractionError matches, so they never start a sign-in popup.
export const AuthErrorCodes = {
  //a Microsoft Graph or Power BI token was requested for a user who did not sign in with Microsoft Entra ID
  MicrosoftIdentityRequired: 'microsoft_identity_required',
  //Google refused the sign-in on the organization's behalf: the Workspace admin has not allowed the app
  OidcAdminPolicy: 'oidc_admin_policy',
  //the Google session cannot be renewed or the sign-in response could not be validated: sign in again
  OidcSignInAgain: 'oidc_sign_in_again',
  //the organization sign-in has no allow-listed issuer for the directory or e-mail domain asked for
  OidcIssuerUnknown: 'oidc_issuer_unknown',
} as const;

//an error the identity layer of the app raised itself, shown as a blocking message with its own text
export const isIdentityAuthError = (error: { code?: string } | undefined): boolean => {
  return (
    error?.code === AuthErrorCodes.MicrosoftIdentityRequired ||
    error?.code === AuthErrorCodes.OidcAdminPolicy ||
    error?.code === AuthErrorCodes.OidcSignInAgain ||
    error?.code === AuthErrorCodes.OidcIssuerUnknown
  );
};

export const isMicrosoftIdentityRequiredError = (error: unknown): boolean => {
  const err = error as { code?: string; message?: string } | undefined;
  if (!err) return false;

  return (
    err.code === AuthErrorCodes.MicrosoftIdentityRequired ||
    (err.message ?? '').indexOf(AuthErrorCodes.MicrosoftIdentityRequired) > -1
  );
};

export const isAuthUserError = (error: Error): boolean => {
  //these user errors can be ignored
  if (!error.message || error.message.length <= 0) {
    return false;
  }

  return (
    error.message.indexOf('user_cancelled') > -1 ||
    error.message.indexOf('interaction_in_progress') > -1 ||
    error.message.indexOf('consent_required') > -1
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isPopupWindowError = (error: any): boolean => {
  if (!error || !error.message) {
    return false;
  }

  return error.message.indexOf('popup_window_error') > -1 || error.message.indexOf('block_iframe_reload') > -1;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isConsentRaceError = (error: any): boolean => {
  // AADSTS650051: during a consent action Entra reports that the app's service principal is already
  // present in the tenant. Consent is therefore effectively granted; this is a transient provisioning
  // race, so the sign-in can simply be retried and it must not raise a support ticket.
  if (!error?.message || error.message.length <= 0) {
    return false;
  }

  return error.message.indexOf('AADSTS650051') > -1;
};

export const isAuthInteractionError = (error: Error): boolean => {
  //these errors indicate that user interaction is required
  if (!error.message || error.message.length <= 0) {
    return false;
  }

  return (
    error.message.indexOf('interaction_required') > -1 ||
    error.message.indexOf('consent_required') > -1 ||
    error.message.indexOf('login_required') > -1 ||
    error.message.indexOf('no_account_in_silent_request') > -1 ||
    error.message.indexOf('invalid_grant') > -1 ||
    error.message.indexOf('block_iframe_reload') > -1 ||
    error.message.indexOf('monitor_window_timeout') > -1 ||
    error.message.indexOf('endpoints_resolution_error') > -1
  );
};
